import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, MessageCircle } from "lucide-react";
import { useAuth } from "../lib/auth";

// Lightweight CSS-only 3D-ish mascot — no heavy WebGL. Renders under 5 KB.
export default function AuraMascot() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => localStorage.getItem("aftermind_mascot_off") === "1");
  const [greeted, setGreeted] = useState(false);

  useEffect(() => {
    if (hidden) return;
    const t = setTimeout(() => setGreeted(true), 1200);
    const t2 = setTimeout(() => setGreeted(false), 6500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [hidden]);

  if (hidden) return null;

  const close = (e) => {
    e.stopPropagation();
    setHidden(true);
    localStorage.setItem("aftermind_mascot_off", "1");
  };
  const open = () => {
    if (user) nav("/app");
    else nav("/");
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 select-none" data-testid="aura-mascot">
      {greeted && (
        <div className="mascot-bubble mb-2 mr-1 ml-auto glass-strong rounded-2xl px-3 py-2 text-sm text-slate-100 max-w-[220px] shadow-xl shadow-violet-500/20">
          Hi, I'm AfterMind. Tell me what you want to create.
        </div>
      )}
      <button
        onClick={open}
        data-testid="mascot-open"
        aria-label="Open AfterMind assistant"
        className="mascot-btn group relative w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full shadow-2xl shadow-violet-500/40 cursor-pointer"
      >
        {/* Orbit ring */}
        <span className="absolute inset-0 rounded-full border border-violet-400/25 mascot-orbit" />
        <span className="absolute -inset-1 rounded-full border border-cyan-400/15 mascot-orbit-slow" />

        {/* Robot body */}
        <span className="absolute inset-1 rounded-full bg-gradient-to-br from-[#1a1046] via-[#2a1466] to-[#0b1240] flex items-center justify-center">
          <span className="mascot-head relative w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-gradient-to-b from-slate-100 to-slate-300 shadow-inner overflow-hidden">
            {/* Visor */}
            <span className="absolute left-1 right-1 top-2.5 h-3 rounded-md bg-gradient-to-r from-violet-500 via-cyan-400 to-violet-500 opacity-90 mascot-visor" />
            {/* Eye highlight */}
            <span className="absolute left-3 top-3 w-1.5 h-1.5 rounded-full bg-white mascot-blink" />
            <span className="absolute right-3 top-3 w-1.5 h-1.5 rounded-full bg-white mascot-blink" />
            {/* Antenna */}
            <span className="absolute left-1/2 -top-2 -translate-x-1/2 w-[3px] h-2 bg-slate-300" />
            <span className="absolute left-1/2 -top-3 -translate-x-1/2 w-2 h-2 rounded-full bg-violet-400 mascot-antenna" />
            {/* Mouth */}
            <span className="absolute left-1/2 bottom-1.5 -translate-x-1/2 w-3 h-[3px] rounded-full bg-slate-500/60" />
          </span>
        </span>

        {/* Sparkle badge */}
        <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-violet-500/40">
          <Sparkles className="w-3 h-3 text-white" strokeWidth={3} />
        </span>
      </button>

      <button
        onClick={close}
        data-testid="mascot-close"
        aria-label="Hide assistant"
        className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-slate-900 border border-white/15 text-slate-300 hover:text-white hover:border-violet-400 flex items-center justify-center transition"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
